import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import type { IslandProps } from '../registry'
import { Badge, Button, EmptyState, Skeleton } from '../../shared/ui'
import { toast } from '../../lib/toast'
import {
  createBusiness,
  fetchOverview,
  getActiveWorkspaceId,
  setActiveWorkspace,
  type OverviewBusiness,
  type WorkspaceOverview,
  type WorkspaceSummary,
} from '../../lib/workspace'
import { useWorkspaces } from './useWorkspaces'
import { validateBusinessName } from './validation'
import { BUSINESS_CSS } from './businessStyles'
import {
  businessEmail,
  describeBusinessEmail,
  type BusinessEmail,
  type BusinessSource,
} from '../../lib/sources'
import { EmailSourceCard } from '../sources/EmailSourceCard'
import { useBusinessSources } from '../sources/useBusinessSources'
import { SOURCES_CSS } from '../sources/sourcesStyles'

type View = 'businesses' | 'all'

const nf = new Intl.NumberFormat('en-US')

function fmt(n: number): string {
  return nf.format(n)
}

function closePage(): void {
  window.closeBusinessesPage?.()
}

export default function BusinessesIsland(_props: IslandProps) {
  const { workspaces, status, error, refetch } = useWorkspaces()
  const { sources, refetch: refetchSources } = useBusinessSources()
  const [view, setView] = useState<View>('businesses')
  const [formOpen, setFormOpen] = useState(false)
  // Onboarding: the business just created, now on its "connect email" step.
  const [onboarding, setOnboarding] = useState<{ id: string; name: string } | null>(null)
  // A business whose email is being set up from its card.
  const [settingUp, setSettingUp] = useState<string | null>(null)

  const activeId = getActiveWorkspaceId()
  const chooseMode =
    status === 'success' ? !activeId || !workspaces.some((w) => w.id === activeId) : !activeId

  return (
    <div className="biz-page">
      <style>{BUSINESS_CSS}</style>
      <style>{SOURCES_CSS}</style>
      <header className="biz-head">
        <h2 className="biz-title">Businesses</h2>
        {!chooseMode && (
          <Button variant="secondary" onClick={closePage}>
            Close
          </Button>
        )}
      </header>

      {chooseMode && <p className="biz-sub">Pick a business to continue.</p>}

      <div className="biz-toolbar">
        <div role="group" aria-label="View" className="biz-toolbar">
          <Button
            variant={view === 'businesses' ? 'primary' : 'secondary'}
            aria-pressed={view === 'businesses'}
            onClick={() => setView('businesses')}
          >
            Businesses
          </Button>
          <Button
            variant={view === 'all' ? 'primary' : 'secondary'}
            aria-pressed={view === 'all'}
            onClick={() => setView('all')}
          >
            All businesses
          </Button>
        </div>
        <Button variant="secondary" onClick={() => setFormOpen(true)} disabled={formOpen}>
          New business
        </Button>
      </div>

      {formOpen && !onboarding && (
        <NewBusinessForm
          onCancel={() => setFormOpen(false)}
          onCreated={(b) => {
            setOnboarding(b)
            refetch()
            refetchSources()
          }}
        />
      )}

      {onboarding && (
        <OnboardingEmailStep
          created={onboarding}
          business={businessEmail(sources, onboarding.id)}
          onChanged={refetchSources}
        />
      )}

      {settingUp && (
        <SetupEmailPanel
          business={businessEmail(sources, settingUp)}
          onChanged={refetchSources}
          onClose={() => setSettingUp(null)}
        />
      )}

      {view === 'businesses' ? (
        <BusinessCards
          workspaces={workspaces}
          status={status}
          error={error}
          chooseMode={chooseMode}
          onRetry={refetch}
          onNew={() => setFormOpen(true)}
          sources={sources}
          onSetupEmail={setSettingUp}
        />
      ) : (
        <AllBusinesses />
      )}
    </div>
  )
}

// ── new business form ────────────────────────────────────────────────────

function NewBusinessForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (b: { id: string; name: string }) => void
}) {
  const [name, setName] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    const problem = validateBusinessName(name)
    if (problem) {
      setFormError(problem)
      return
    }
    setFormError(null)
    setBusy(true)
    try {
      const created = await createBusiness(name.trim())
      setBusy(false)
      onCreated(created)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not create the business.'
      setFormError(msg)
      toast(msg, 'error')
      setBusy(false)
    }
  }

  return (
    <form className="biz-form" onSubmit={(e) => void onSubmit(e)} noValidate>
      <div className="biz-field">
        <label className="biz-label" htmlFor="biz-new-name">
          Business name
        </label>
        <input
          id="biz-new-name"
          className="settings-input"
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={formError ? true : undefined}
          aria-describedby={formError ? 'biz-new-error' : undefined}
        />
      </div>
      <Button type="submit" disabled={busy}>
        {busy ? 'Creating…' : 'Create'}
      </Button>
      <Button variant="secondary" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      {formError && (
        <p id="biz-new-error" className="biz-error" role="alert">
          {formError}
        </p>
      )}
    </form>
  )
}

// ── cards ────────────────────────────────────────────────────────────────

interface CardsProps {
  workspaces: WorkspaceSummary[]
  status: 'idle' | 'loading' | 'success' | 'error'
  error: string | null
  chooseMode: boolean
  onRetry: () => void
  onNew: () => void
  sources: BusinessSource[]
  onSetupEmail: (workspaceId: string) => void
}

function BusinessCards({
  workspaces,
  status,
  error,
  chooseMode,
  onRetry,
  onNew,
  sources,
  onSetupEmail,
}: CardsProps) {
  if (status === 'idle' || status === 'loading') return <LoadingBlock />
  if (status === 'error') {
    return (
      <EmptyState
        role="alert"
        title="Could not load your businesses"
        message={error ?? 'Something went wrong.'}
        cta={{ label: 'Retry', onClick: onRetry }}
      />
    )
  }
  if (workspaces.length === 0) {
    return (
      <EmptyState
        title="No businesses yet"
        message="Create your first business to start adding contacts."
        cta={{ label: 'New business', onClick: onNew }}
      />
    )
  }
  return (
    <ul className="biz-grid" aria-label="Your businesses">
      {workspaces.map((w) => (
        <BusinessCard
          key={w.id}
          ws={w}
          chooseMode={chooseMode}
          email={businessEmail(sources, w.id)}
          onSetupEmail={() => onSetupEmail(w.id)}
        />
      ))}
    </ul>
  )
}

function BusinessCard({
  ws,
  chooseMode,
  email,
  onSetupEmail,
}: {
  ws: WorkspaceSummary
  chooseMode: boolean
  email: BusinessEmail | null
  onSetupEmail: () => void
}) {
  // While no business is chosen yet, the server falls back to the personal one
  // and calls it current. Keep Open available so that choice can be confirmed.
  const showOpen = !ws.is_current || chooseMode
  return (
    <li className={`biz-card${ws.is_current ? ' is-current' : ''}`}>
      <div className="biz-card-head">
        <h3 className="biz-card-name">{ws.name}</h3>
        <div className="biz-badges">
          {ws.kind === 'personal' && <Badge>Personal</Badge>}
          {ws.is_current && <Badge tone="ok">Current</Badge>}
        </div>
      </div>
      <dl className="biz-figures">
        <div className="biz-figure">
          <dt>Contacts</dt>
          <dd>{fmt(ws.contact_count)}</dd>
        </div>
        <div className="biz-figure">
          <dt>Leads this week</dt>
          <dd>{fmt(ws.leads_this_week)}</dd>
        </div>
        <div className="biz-figure">
          <dt>Sends today</dt>
          <dd>{fmt(ws.sends_today)}</dd>
        </div>
      </dl>
      {email && (
        <p className="biz-source">
          <span className="src-kind">Email</span> {describeBusinessEmail(email)}
        </p>
      )}
      <div className="biz-actions">
        {showOpen && (
          <Button aria-label={`Open ${ws.name}`} onClick={() => setActiveWorkspace(ws.id)}>
            Open
          </Button>
        )}
        {email && (
          <Button variant="secondary" aria-label={`Email for ${ws.name}`} onClick={onSetupEmail}>
            {email.mailboxes.length > 0 ? 'Manage email' : 'Connect email'}
          </Button>
        )}
      </div>
    </li>
  )
}

// ── onboarding: connect the new business's email ────────────────────────

/**
 * Step 2 of creating a business: connect its mailbox, then open it.
 * The business is not switched to until the user clicks Open, so the
 * OAuth popup and this page stay put while they sign in.
 */
function OnboardingEmailStep({
  created: business,
  business: email,
  onChanged,
}: {
  created: { id: string; name: string }
  business: BusinessEmail | null
  onChanged: () => void
}) {
  const connected = (email?.mailboxes.length ?? 0) > 0
  return (
    <section className="biz-onboard" aria-labelledby="biz-onboard-title">
      <ol className="biz-steps" aria-label="Setup steps">
        <li className="is-done">Name the business</li>
        <li className={connected ? 'is-done' : 'is-active'}>Connect its email</li>
        <li className={connected ? 'is-active' : ''}>Open it and import contacts</li>
      </ol>
      <h3 className="biz-card-name" id="biz-onboard-title">
        {business.name} is ready. Connect its email.
      </h3>
      <p className="biz-sub">
        Hunts sends from these addresses and shows replies on each contact. Connect every inbox this
        business sends from; you can add more later in Settings &gt; Email.
      </p>
      {email ? (
        <ul className="src-list">
          <EmailSourceCard business={email} onChanged={onChanged} compact />
        </ul>
      ) : (
        <Skeleton />
      )}
      <div className="biz-actions">
        <Button onClick={() => setActiveWorkspace(business.id)}>
          {connected ? `Open ${business.name}` : `Open ${business.name} without email`}
        </Button>
      </div>
    </section>
  )
}

function SetupEmailPanel({
  business: email,
  onChanged,
  onClose,
}: {
  business: BusinessEmail | null
  onChanged: () => void
  onClose: () => void
}) {
  if (!email) return null
  return (
    <section className="biz-onboard" aria-label={`Email for ${email.workspace_name}`}>
      <div className="biz-head">
        <h3 className="biz-card-name">Email for {email.workspace_name}</h3>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </div>
      <ul className="src-list">
        <EmailSourceCard business={email} onChanged={onChanged} compact />
      </ul>
    </section>
  )
}

function LoadingBlock() {
  return (
    <div className="biz-skeletons" aria-busy="true" aria-label="Loading">
      <Skeleton width="40" />
      <Skeleton />
      <Skeleton width="70" />
    </div>
  )
}

// ── all businesses ───────────────────────────────────────────────────────

function AllBusinesses() {
  const [overview, setOverview] = useState<WorkspaceOverview | null>(null)
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const retry = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setError(null)
    void (async () => {
      try {
        const data = await fetchOverview()
        if (cancelled) return
        setOverview(data)
        setStatus('success')
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Could not load the overview.')
        setStatus('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tick])

  if (status === 'loading') return <LoadingBlock />
  if (status === 'error' || !overview) {
    return (
      <EmptyState
        role="alert"
        title="Could not load the overview"
        message={error ?? 'Something went wrong.'}
        cta={{ label: 'Retry', onClick: retry }}
      />
    )
  }

  const t = overview.totals
  const totals: Array<[string, number]> = [
    ['Contacts', t.contacts],
    ['Leads 7d', t.leads_7d],
    ['Sends today', t.sends_today],
    ['Sends 7d', t.sends_7d],
    ['Replies 7d', t.replies_7d],
    ['Open deals', t.open_deals],
    ['Pipeline value', t.pipeline_value],
    ['Open tasks', t.open_tasks],
  ]

  return (
    <>
      <dl className="biz-totals" role="group" aria-label="Totals">
        {totals.map(([label, value]) => (
          <div key={label} className="biz-total">
            <dt>{label}</dt>
            <dd>{fmt(value)}</dd>
          </div>
        ))}
      </dl>
      <div className="table-wrap">
        <table aria-label="Businesses overview">
          <thead>
            <tr>
              <th>Business</th>
              <th className="biz-num">Contacts</th>
              <th className="biz-num">Leads 7d</th>
              <th className="biz-num">Sends today</th>
              <th className="biz-num">Sends 7d</th>
              <th className="biz-num">Replies 7d</th>
              <th className="biz-num">Open deals</th>
              <th className="biz-num">Pipeline value</th>
              <th className="biz-num">Open tasks</th>
            </tr>
          </thead>
          <tbody>
            {overview.businesses.map((b) => (
              <OverviewRow key={b.id} b={b} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function OverviewRow({ b }: { b: OverviewBusiness }) {
  return (
    <tr>
      <td>
        {b.name} {b.kind === 'personal' && <Badge>Personal</Badge>}
      </td>
      <td className="biz-num">{fmt(b.contacts)}</td>
      <td className="biz-num">{fmt(b.leads_7d)}</td>
      <td className="biz-num">{fmt(b.sends_today)}</td>
      <td className="biz-num">{fmt(b.sends_7d)}</td>
      <td className="biz-num">{fmt(b.replies_7d)}</td>
      <td className="biz-num">{fmt(b.open_deals)}</td>
      <td className="biz-num">{fmt(b.pipeline_value)}</td>
      <td className="biz-num">{fmt(b.open_tasks)}</td>
    </tr>
  )
}
